import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

const EVENT_TYPES = [
    "birthday-parties","private-parties","corporate-events","meetings","weddings",
    "private-dining","christmas-parties","engagement-parties","baby-showers",
    "product-launches","networking-events","conferences","workshops","training-days",
    "filming","photoshoots","pop-up-events","charity-events"
];

const label = (s) => s.split("-").map(w => w[0].toUpperCase()+w.slice(1)).join(" ");

export default function EnquiryForm({ venueId, venueName }) {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", event_type: "",
        event_date: "", guests: "", budget: "", message: "",
        flexible_date: false, consent: true, website_hp: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email and message");
            return;
        }
        if (!form.consent) {
            toast.error("Please accept the consent to continue");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/enquiries", {
                venue_id: venueId,
                name: form.name, email: form.email, phone: form.phone,
                event_type: form.event_type || undefined,
                event_date: form.event_date || undefined,
                flexible_date: form.flexible_date,
                guests: form.guests ? Number(form.guests) : undefined,
                budget: form.budget || undefined,
                message: form.message,
                consent: form.consent,
                website_hp: form.website_hp,
                source_page: window.location.pathname,
            });
            setDone(true);
            toast.success("Enquiry sent!");
        } catch (err) {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="rounded-xl border border-border p-6 bg-accent/40 text-center" data-testid="enquiry-thank-you">
                <CheckCircle2 className="h-10 w-10 mx-auto text-primary" />
                <h3 className="font-display text-xl mt-3">Enquiry sent!</h3>
                <p className="text-sm text-muted-foreground mt-2">
                    We've forwarded your details to {venueName}. They will get back to you shortly by email.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-3" data-testid="enquiry-form">
            {/* Honeypot */}
            <input type="text" name="website" value={form.website_hp} onChange={(e) => set("website_hp", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />
            <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                    <Label htmlFor="name">Your name</Label>
                    <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="enquiry-name" />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="enquiry-email" />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="enquiry-phone" />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Label>Event type</Label>
                    <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                        <SelectTrigger data-testid="enquiry-event-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                            {EVENT_TYPES.map((et) => <SelectItem key={et} value={et}>{label(et)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="date">Event date</Label>
                    <Input id="date" type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="enquiry-date" />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="guests">Number of guests</Label>
                    <Input id="guests" type="number" min="1" value={form.guests} onChange={(e) => set("guests", e.target.value)} data-testid="enquiry-guests" />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="budget">Budget</Label>
                    <Input id="budget" placeholder="e.g. £2,000" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="enquiry-budget" />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                    <Checkbox id="flex" checked={form.flexible_date} onCheckedChange={(v) => set("flexible_date", !!v)} data-testid="enquiry-flex" />
                    <label htmlFor="flex" className="text-sm text-muted-foreground">Dates are flexible</label>
                </div>
                <div className="col-span-2">
                    <Label htmlFor="msg">Message</Label>
                    <Textarea id="msg" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} required data-testid="enquiry-message" />
                </div>
                <div className="col-span-2 flex items-start gap-2">
                    <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="enquiry-consent" />
                    <label htmlFor="consent" className="text-xs text-muted-foreground leading-snug">
                        I agree to Venuely's Terms and Privacy Policy and consent to being contacted by this venue.
                    </label>
                </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="enquiry-submit">
                {submitting ? "Sending…" : "Send enquiry"}
            </Button>
        </form>
    );
}
